import { Typography } from '@design-system/components/typography/typography';
import { Divider, Switch } from '@mui/material';
import { OtpBundlesVMData } from '../viewModel/otpBundles.viewModel';
import { faDollar } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@design-system/components/button/button';
import { SideModal } from '@design-system/components/modal/sideModal';
import { Input } from '@design-system/components/input/input';
import { InputPrice } from '@design-system/components/input/inputPrice';
import { sprinkles } from '@design-system/sprinkles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPercent } from '@fortawesome/pro-regular-svg-icons';
import { Controller } from 'react-hook-form';
import { Alert } from '@design-system/components/alert/alert';
import { Textarea } from '@design-system/components/textarea/textarea';
import { ContentSection } from './ContentSection';
import { FeaturesSection } from './FeaturesSection';
import { InputNumber } from '@design-system/components/input/inputNumber';
import clsx from 'clsx';
import { TooltipWrapper } from '../../view/TooltipWrapper';
import { offerPriceTransformation } from '../viewModel/otpBundle.vm.mapper';

export const BundleForm = ({
  title,
  open,
  data,
  cancel,
  control,
  cta,
  formError,
  showFeeControl,
  readonly,
  formErrorTitle,
  discounts,
  isFreeDisabled,
  isOfferFree,
  onChangeIsFree,
}: OtpBundlesVMData['formData']) => (
  <SideModal isOpen={open} onClose={() => cancel()} noSpacing>
    <div className="flex flex-col px-12 py-6 gap-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <Typography variant="title">{title}</Typography>
        <div className="flex gap-4">
          {cta.header.map(({ label, onClick, color, disabled, tooltip }) => (
            <TooltipWrapper key={label} tooltip={tooltip}>
              <Button
                color={color || 'primary'}
                onClick={onClick}
                disabled={disabled}
              >
                {label}
              </Button>
            </TooltipWrapper>
          ))}
        </div>
      </div>
      {formError && (
        <Alert
          variant="error"
          details={formError}
          title={formErrorTitle || 'Offer cannot be published'}
        />
      )}
      <div className="flex flex-col gap-6">
        <Controller
          name={'name'}
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <Input
              label="Name"
              required={true}
              value={value}
              onChange={onChange}
              error={fieldState.error?.message}
              disabled={readonly}
            />
          )}
        />
        <div className="flex flex-col md:flex-row gap-6 items-center">
          <Controller
            name={'price'}
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <InputPrice
                label="Price"
                required={!isOfferFree}
                value={value}
                onChange={(value) => onChange(offerPriceTransformation(value))}
                startIcon={{
                  icon: faDollar,
                  className: sprinkles({ color: 'N-80' }),
                }}
                supportingText="Min. value - $0.99"
                placeholder="00.00"
                hasError={!!fieldState.error?.message}
                error={fieldState.error?.message}
                disabled={readonly || isOfferFree}
              />
            )}
          />
          <div className="flex gap-2">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className={sprinkles({ color: 'N-80' })}
            />
            <Typography
              variant="body"
              size="medium"
              fontWeight="normal"
              color="N-80"
            >
              This is a One-Time Purchase Offer. Your user will pay the price
              once to get a lifetime access to selected content.
            </Typography>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-6 items-center">
          <Controller
            name={'isFree'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="flex items-center gap-2 min-w-[245px]">
                <Switch
                  checked={value}
                  onChange={(event, checked) => {
                    onChange(event);
                    onChangeIsFree(checked);
                  }}
                  color="primary"
                  disabled={isFreeDisabled}
                  slotProps={{
                    input: {
                      name: 'free',
                    },
                  }}
                />
                <Typography
                  variant="body"
                  size="large"
                  fontWeight="normal"
                  className={sprinkles({
                    color: isFreeDisabled ? 'N-60' : 'B-100',
                  })}
                >
                  Free offer
                </Typography>
              </div>
            )}
          />
          <div className="flex gap-2">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className={sprinkles({ color: 'N-80' })}
            />
            <Typography
              variant="body"
              size="medium"
              fontWeight="normal"
              color="N-80"
            >
              Offering free access allows users to enjoy selected content for
              life at no cost.
            </Typography>
          </div>
        </div>
        {!discounts || isOfferFree ? null : (
          <div className="flex items-center justify-between">
            <Typography variant="body" size="medium">
              Discounts created: {discounts.count}
            </Typography>
            <div className="flex items-center justify-end gap-6">
              {discounts.count > 0 ? (
                <Typography
                  variant="body"
                  size="medium"
                  color="P-80"
                  className="cursor-pointer"
                  onClick={discounts.onShow}
                >
                  Show all
                </Typography>
              ) : null}
              {!readonly && (
                <Typography
                  variant="body"
                  size="medium"
                  color="P-80"
                  className="cursor-pointer"
                  onClick={discounts.onCreate}
                >
                  Create new discount
                </Typography>
              )}
            </div>
          </div>
        )}
        {showFeeControl && (
          <Controller
            name={'playbookFee'}
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <InputNumber
                label="Playbook fee"
                value={value}
                onChange={onChange}
                error={fieldState.error?.message}
                endIcon={{ icon: faPercent }}
                wrapperClassName="w-[160px]"
                disabled={readonly}
              />
            )}
          />
        )}
        <Divider />
        <div className="flex flex-col gap-6">
          <Controller
            name={'features'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <FeaturesSection
                features={data.features}
                disabled={readonly && !isOfferFree}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <Controller
            name={'programs'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <ContentSection
                {...data.programs}
                disabled={readonly && !isOfferFree}
                value={value || []}
                onChange={onChange}
              />
            )}
          />
          <Controller
            name={'content'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <ContentSection
                {...data.content}
                disabled={readonly && !isOfferFree}
                value={value || []}
                onChange={onChange}
              />
            )}
          />
        </div>
        <Controller
          name={'description'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Textarea
              label="Description"
              value={value}
              onChange={onChange}
              disabled={readonly}
            />
          )}
        />
      </div>
      {cta.footer && (
        <div className="flex gap-4 flex-col">
          {cta.footer.map(
            ({
              label,
              onClick,
              color,
              variant,
              className,
              disabled,
              infoText,
            }) => (
              <div key={label} className="w-full h-full flex gap-4">
                <Button
                  color={color || 'primary'}
                  variant={variant || 'filled'}
                  onClick={onClick}
                  className={className}
                  disabled={disabled}
                >
                  {label}
                </Button>
                {infoText && <InfoText infoText={infoText} />}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  </SideModal>
);

const InfoText = ({ infoText }: { infoText: string }) => (
  <div className={clsx('flex gap-4', sprinkles({ color: 'N-80' }))}>
    <FontAwesomeIcon icon={faInfoCircle} className="text-[14px]" />
    <Typography variant="body" size="medium" fontWeight="normal">
      {infoText}
    </Typography>
  </div>
);
